'use client';

import dynamic from 'next/dynamic';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { GlobeLoadingPlaceholder } from '@/components/ui/ContentGlobe/GlobeLoadingPlaceholder';
import { isWebGLAvailable } from '@/components/ui/ContentGlobe/webgl-support';
import { Heading } from '@/components/ui/heading';
import { Text } from '@/components/ui/text';
import { ErrorBoundary } from '@/shared/components/error-boundary';

const SatelliteGlobe = dynamic(
  () => import('@/components/ui/ContentGlobe/SatelliteGlobe').then((m) => m.SatelliteGlobe),
  {
    ssr: false,
    loading: () => <GlobeLoadingPlaceholder height="600px" minHeight="400px" />,
  }
);

interface CreatorGlobeSectionProps {
  handler: string;
  creatorId: number;
}

function GlobeUnavailable() {
  return (
    <div className="flex min-h-[400px] w-full flex-col items-center justify-center gap-4 px-4 text-center">
      <Text variant="body-sm" color="muted">
        This browser can&apos;t render the 3D globe. Open Taboo TV in Safari or Chrome, or browse
        creators the classic way.
      </Text>
      <Button asChild variant="outline">
        <Link href="/creators">Browse creators</Link>
      </Button>
    </div>
  );
}

export function CreatorGlobeSection({ handler, creatorId }: CreatorGlobeSectionProps) {
  // Capability read is client-only; first render must match the server (two-pass gate).
  const [ready, setReady] = useState(false);
  useEffect(() => setReady(true), []);
  const unsupported = ready && !isWebGLAvailable();

  return (
    <section className="flex flex-col items-center py-12">
      <div className="flex flex-col mb-12 items-center">
        <Heading level={2} size="section" className="text-white mb-3 text-center">
          Discover creators worldwide
        </Heading>
        <Text variant="body-sm" color="muted" className="mb-7 text-center px-4">
          Explore our interactive globe to find content from around the world.
        </Text>
      </div>

      {unsupported ? (
        <GlobeUnavailable />
      ) : (
        <ErrorBoundary fallback={<GlobeUnavailable />}>
          <SatelliteGlobe
            creatorId={creatorId}
            creatorHandle={handler}
            maxMarkers={100}
            height="600px"
            minHeight="400px"
            ariaLabel="Interactive world globe showing creator locations"
          />
        </ErrorBoundary>
      )}
    </section>
  );
}
